import { DestroyRef, Injectable, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, Subject } from 'rxjs';
import { API_PREFIX, resolveUrl } from 'src/core/constants/api.constants';
import { CampaignProgressDto } from 'src/core/models/campaign/campaign-progress.dto';
import { HubService } from './hub.service';

const AGENT_HEARTBEAT_HUB_URL = resolveUrl('/api/hubs/agent-heartbeat');
const AGENT_HEARTBEAT_UNREGISTER_URL = resolveUrl(`${API_PREFIX}/agent-heartbeat/unregister`);

@Injectable({ providedIn: 'root' })
export class AgentHeartbeatService extends HubService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly oauthService = inject(OAuthService);
  private readonly unloadEvents = ['pagehide', 'beforeunload'] as const;
  private readonly pageExitHandler = () => this.handlePageExit();
  private readonly agentStatusChanged = new Subject<number>();
  private readonly hubReconnectedSource = new Subject<void>();
  private readonly campaignProgressChanged = new Subject<CampaignProgressDto>();
  private readonly campaignPoolExhausted = new Subject<number>();
  private sessionId: number | null = null;
  private isPageExitInProgress = false;
  private arePageExitHandlersRegistered = false;
  private joinedCampaignId: number | null = null;

  /** Emits the new statusId whenever the agent's status changes (broadcast to all tabs). */
  readonly agentStatusChanged$: Observable<number> = this.agentStatusChanged.asObservable();
  /** Emits after the hub successfully reconnects and re-registers. */
  readonly hubReconnected$: Observable<void> = this.hubReconnectedSource.asObservable();
  /** Emits the latest real-time progress snapshot for a campaign the agent is running. */
  readonly campaignProgressChanged$: Observable<CampaignProgressDto> =
    this.campaignProgressChanged.asObservable();
  /** Emits the campaign id whenever a campaign's reservable contact pool becomes exhausted. */
  readonly campaignPoolExhausted$: Observable<number> = this.campaignPoolExhausted.asObservable();

  constructor() {
    super();
    this.initialize({ url: AGENT_HEARTBEAT_HUB_URL });
    this.destroyRef.onDestroy(() => this.unregisterPageExitHandlers());
  }

  async connect(sessionId: number): Promise<void> {
    this.registerPageExitHandlers();
    this.sessionId = sessionId;
    this.isPageExitInProgress = false;

    if (this.isConnected()) {
      await this.register();
      return;
    }

    await this.startConnection();
  }

  async disconnect(): Promise<void> {
    this.sessionId = null;
    this.isPageExitInProgress = false;
    await this.stopConnection('Unregister');
    this.unregisterPageExitHandlers();
  }

  protected override async onConnected(): Promise<void> {
    this.hubConnection?.off('OnAgentStatusChanged');
    this.hubConnection?.on('OnAgentStatusChanged', (statusId: number) => {
      this.agentStatusChanged.next(statusId);
    });
    this.hubConnection?.off('OnCampaignProgressChanged');
    this.hubConnection?.on('OnCampaignProgressChanged', (progress: CampaignProgressDto) => {
      this.campaignProgressChanged.next(progress);
    });
    this.hubConnection?.off('OnCampaignContactPoolExhausted');
    this.hubConnection?.on('OnCampaignContactPoolExhausted', (campaignId: number) => {
      this.campaignPoolExhausted.next(campaignId);
    });
    await this.register();
  }

  protected override async onReconnected(): Promise<void> {
    await this.register();
    if (this.joinedCampaignId != null) {
      void this.invoke('JoinCampaignGroup', this.joinedCampaignId);
    }
    this.hubReconnectedSource.next();
  }

  setActiveSegment(segmentId: number): void {
    void this.invoke('SetActiveSegment', segmentId);
  }

  /**
   * Joins the per-campaign SignalR group so this connection receives that campaign's real-time
   * progress/pool-exhausted pushes. The group is remembered and re-joined after a reconnect.
   * @param campaignId Identifier of the campaign whose group to join.
   */
  joinCampaignGroup(campaignId: number): void {
    this.joinedCampaignId = campaignId;
    void this.invoke('JoinCampaignGroup', campaignId);
  }

  /**
   * Leaves the per-campaign SignalR group and clears the remembered campaign so a reconnect no longer
   * re-joins it.
   * @param campaignId Identifier of the campaign whose group to leave.
   */
  leaveCampaignGroup(campaignId: number): void {
    this.joinedCampaignId = null;
    void this.invoke('LeaveCampaignGroup', campaignId);
  }

  private async register(): Promise<void> {
    if (this.sessionId == null) {
      return;
    }

    await this.invoke('Register', this.sessionId);
  }

  private registerPageExitHandlers(): void {
    if (typeof window === 'undefined' || this.arePageExitHandlersRegistered) {
      return;
    }

    for (const eventName of this.unloadEvents) {
      window.addEventListener(eventName, this.pageExitHandler);
    }

    this.arePageExitHandlersRegistered = true;
  }

  private unregisterPageExitHandlers(): void {
    if (typeof window === 'undefined' || !this.arePageExitHandlersRegistered) {
      return;
    }

    for (const eventName of this.unloadEvents) {
      window.removeEventListener(eventName, this.pageExitHandler);
    }

    this.arePageExitHandlersRegistered = false;
  }

  private handlePageExit(): void {
    if (this.isPageExitInProgress || this.sessionId == null) {
      return;
    }

    const connectionId = this.getConnectionId();
    if (!connectionId) {
      return;
    }

    this.isPageExitInProgress = true;
    this.sessionId = null;
    this.sendUnloadRequest(connectionId);
    this.abandonConnection();
  }

  private sendUnloadRequest(connectionId: string): void {
    const accessToken = this.oauthService.getAccessToken();
    if (!accessToken || typeof fetch === 'undefined') {
      return;
    }

    const request: RequestInit = {
      method: 'POST',
      keepalive: true,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ connectionId }),
    };

    try {
      void fetch(AGENT_HEARTBEAT_UNREGISTER_URL, request);
    } catch {
      // Ignore unload-time transport errors.
    }
  }
}
